import { Autorenew } from '@mui/icons-material';
import { Button, Skeleton, Stack, ToggleButton, Typography } from '@mui/material';
import { FC, useRef } from 'react';

import useLocalStorage from 'src/hooks/useLocalStorage';
import { useGetLockerCellsSizesQuery } from 'src/redux/api/lockers';
import { LockerBankSummary, LockerCellSizeAvailability } from 'src/types/apiLockers';
import VerticalToggleButtonGroup from 'src/uiKit/VerticalToggleButtonGroup';

type Props = {
  lockerBank: LockerBankSummary;
  onChange: (cellSize: string) => void;
  selectedCellSize?: string;
  disabled?: boolean;
};

type ContentProps = {
  sizes?: LockerCellSizeAvailability[];
  isLoading: boolean;
  onChange: (cellSize: string) => void;
  selectedCellSize?: string;
  disabled?: boolean;
};

const StyledSkeleton = () => <Skeleton sx={{ height: '54px' }} />;

const Content: FC<ContentProps> = ({ isLoading, onChange, sizes, selectedCellSize, disabled }) => {
  const onChangeValue = (_: any, value: string) => onChange(value);

  switch (true) {
    case isLoading:
      return (
        <>
          <Typography variant="subtitle2">Locker size</Typography>
          <StyledSkeleton />
          <StyledSkeleton />
          <StyledSkeleton />
        </>
      );

    case !!sizes && !!sizes.length:
      return (
        <>
          <Typography variant="subtitle2">Locker size</Typography>
          <VerticalToggleButtonGroup disabled={disabled} exclusive orientation="vertical" value={selectedCellSize} onChange={onChangeValue}>
            {sizes?.map((item) => (
              <ToggleButton color="primary" size="large" value={item.size} key={item.size}>
                {item.size} ({item.availableCount})
              </ToggleButton>
            ))}
          </VerticalToggleButtonGroup>
        </>
      );

    default:
      return <Typography color="text.secondary">No locker cells available</Typography>;
  }
};

const CellsSizeSelect: FC<Props> = ({ lockerBank, selectedCellSize, onChange, disabled }) => {
  const { data, isLoading, isFetching, refetch } = useGetLockerCellsSizesQuery(lockerBank.id, {
    refetchOnMountOrArgChange: true,
  });

  const isSetDefault = useRef(false);
  const [lastLockerSize] = useLocalStorage<string>('rd_last_locker_size', '');

  if (!isSetDefault.current && data) {
    if (data.find(({ size }) => size === lastLockerSize)) {
      onChange(lastLockerSize);
      isSetDefault.current = true;
    }
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={0.8}
        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle1">Item</Typography>
        <Button disabled={disabled} variant="soft" size="small" startIcon={<Autorenew />} onClick={refetch}>
          Refresh
        </Button>
      </Stack>
      <Content
        disabled={disabled}
        isLoading={isLoading || isFetching}
        onChange={onChange}
        sizes={data}
        selectedCellSize={selectedCellSize}
      />
    </>
  );
};

export default CellsSizeSelect;
